import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  GitBranch, CheckCircle, Clock, XCircle, Loader2,
  MessageSquare, Share2, ArrowRight, RefreshCw, Plus,
  BarChart2, FileCode, Users, Zap, TrendingUp, AlertTriangle,
} from 'lucide-react';
import { formatRelativeTime, formatNumber } from '../lib/utils';
import { SEO } from '../components/SEO';
import { API_URL } from '../lib/config';
function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('cp_token')}`, 'Content-Type': 'application/json' };
}

interface Repo {
  _id: string;
  name: string;
  url: string;
  branch: string;
  provider: string;
  indexingStatus: 'pending' | 'in_progress' | 'completed' | 'failed';
  lastIndexedAt?: string;
  fileCount?: number;
  errorMessage?: string;
}

const STATUS_CONFIG = {
  pending:     { icon: Clock,       color: 'text-amber-400',   label: 'Pending' },
  in_progress: { icon: Loader2,     color: 'text-t1',          label: 'Indexing' },
  completed:   { icon: CheckCircle, color: 'text-emerald-400', label: 'Indexed' },
  failed:      { icon: XCircle,     color: 'text-red-400',     label: 'Failed' },
};

const SUGGESTED_QUESTIONS = [
  'What services depend on the authentication service?',
  'Where is the payment processing logic implemented?',
  'Which team owns the API gateway?',
  'What are the recent changes to the user service?',
  'Explain the queue worker architecture',
  'What databases does the payments service use?',
];

/* ─── Stat card ─────────────────────────────────────────────── */

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub?: string;
  trend?: number;
  color?: string;
}

function StatCard({ icon: Icon, label, value, sub, trend, color = 'text-t1' }: StatCardProps) {
  return (
    <div className="card p-5">
      <div className="flex items-start justify-between mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--s4)' }}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-medium ${trend >= 0 ? 'text-emerald-500' : 'text-red-400'}`}>
            <TrendingUp className="w-3 h-3" />
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div className="text-2xl font-bold text-t1 tracking-tight mb-1">{value}</div>
      <div className="text-xs text-t3">{label}</div>
      {sub && <div className="text-xs text-t4 mt-1">{sub}</div>}
    </div>
  );
}

/* ─── Repository row ─────────────────────────────────────────── */

function RepoRow({ repo }: { repo: Repo }) {
  const s = STATUS_CONFIG[repo.indexingStatus];
  const StatusIcon = s.icon;

  return (
    <div className="flex items-center gap-4 py-3 border-b last:border-0 group" style={{ borderColor: 'var(--b-subtle)' }}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-7 h-7 rounded-lg border flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'var(--s4)', borderColor: 'var(--b-subtle)' }}>
          <GitBranch className="w-3.5 h-3.5 text-t3" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-t1 truncate">{repo.name}</div>
          <div className="text-xs text-t4 truncate">
            {repo.branch}
            {repo.fileCount ? ` · ${formatNumber(repo.fileCount)} files` : ''}
            {repo.lastIndexedAt ? ` · synced ${formatRelativeTime(repo.lastIndexedAt)}` : ''}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <StatusIcon className={`w-3.5 h-3.5 ${s.color} ${repo.indexingStatus === 'in_progress' ? 'animate-spin' : ''}`} />
        <span className={`text-xs ${s.color}`}>{s.label}</span>
      </div>
      <Link
        to={`/conversations?repo=${repo._id}`}
        className="opacity-0 group-hover:opacity-100 flex items-center gap-1 text-xs text-t1 hover:opacity-70 transition-all flex-shrink-0"
      >
        <MessageSquare className="w-3 h-3" />
        Ask
      </Link>
    </div>
  );
}

/* ─── Main Dashboard ──────────────────────────────────────────── */

export function DashboardPage() {
  const [repos, setRepos] = useState<Repo[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/repositories`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setRepos(data.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const indexedRepos = repos.filter(r => r.indexingStatus === 'completed');
  const totalFiles   = repos.reduce((s, r) => s + (r.fileCount || 0), 0);
  const failedRepos  = repos.filter(r => r.indexingStatus === 'failed');

  const user = (() => {
    try { return JSON.parse(localStorage.getItem('cp_user') || '{}'); } catch { return {}; }
  })();
  const firstName = (user.name || 'Engineer').split(' ')[0];

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6">
      <SEO title="Dashboard" description="Your CodePilot Engineer dashboard. Monitor repository health, indexed files, and jump into AI conversations about your codebase." noIndex />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-t1 tracking-tight">
            Good morning, {firstName}
          </h1>
          <p className="text-sm text-t3 mt-0.5">
            {repos.length} repositories connected · {indexedRepos.length} indexed
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="btn-ghost">
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
          <Link to="/repositories" className="btn-primary">
            <Plus className="w-4 h-4" />
            Add Repository
          </Link>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={GitBranch}  label="Repositories"       value={repos.length || '—'}       sub={`${indexedRepos.length} indexed`}        color="text-t1" trend={repos.length > 0 ? 12 : undefined} />
        <StatCard icon={FileCode}   label="Files Indexed"      value={totalFiles > 0 ? formatNumber(totalFiles) : '—'} sub="across all repositories" color="text-blue-400" />
        <StatCard icon={BarChart2}  label="Knowledge Coverage" value={indexedRepos.length > 0 ? `${Math.round((indexedRepos.length / repos.length) * 100)}%` : '—'} sub="of codebase documented" color="text-emerald-400" trend={indexedRepos.length > 0 ? 5 : undefined} />
        <StatCard icon={Users}      label="Team Members"       value="1"                          sub="Free plan · up to 1"                     color="text-amber-400" />
      </div>

      {/* Main grid */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Repository health — 2 cols */}
        <div className="lg:col-span-2 card p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-sm font-semibold text-t1">Repository Health</h2>
              <p className="text-xs text-t4 mt-0.5">Status of your connected repositories</p>
            </div>
            <Link to="/repositories" className="btn-ghost text-xs">
              Manage <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-t4" />
            </div>
          ) : repos.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 rounded-xl border flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: 'var(--s1)', borderColor: 'var(--b-subtle)' }}>
                <GitBranch className="w-6 h-6 text-t4" />
              </div>
              <p className="text-sm text-t3 mb-1">No repositories connected</p>
              <p className="text-xs text-t4 mb-4">Connect your first repository to start indexing</p>
              <Link to="/repositories" className="btn-primary text-xs px-3 py-1.5">
                <Plus className="w-3.5 h-3.5" /> Connect repository
              </Link>
            </div>
          ) : (
            <div>
              {repos.slice(0, 8).map(repo => <RepoRow key={repo._id} repo={repo} />)}
              {repos.length > 8 && (
                <Link to="/repositories" className="block text-center text-xs text-t1 hover:opacity-70 pt-3 transition-colors">
                  View all {repos.length} repositories →
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Quick actions */}
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-t1 mb-4">Quick Actions</h2>
            <div className="space-y-2">
              {[
                { icon: MessageSquare, label: 'New conversation',     sub: 'Ask AI about your code',    to: '/conversations?new=1', color: 'text-t1' },
                { icon: Share2,        label: 'Architecture explorer', sub: 'View service dependencies', to: '/architecture',         color: 'text-blue-400' },
                { icon: GitBranch,     label: 'Add repository',        sub: 'Connect a new codebase',    to: '/repositories?add=1',  color: 'text-emerald-400' },
              ].map(a => (
                <Link key={a.label} to={a.to}
                  className="flex items-center gap-3 p-3 rounded-lg border border-transparent transition-all group"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = 'var(--overlay-sm)';
                    e.currentTarget.style.borderColor = 'var(--b-subtle)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--s4)' }}>
                    <a.icon className={`w-3.5 h-3.5 ${a.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-t2 group-hover:text-t1 transition-colors">{a.label}</div>
                    <div className="text-[10px] text-t4">{a.sub}</div>
                  </div>
                  <ArrowRight className="w-3 h-3 text-t4 opacity-0 group-hover:opacity-100 transition-all" />
                </Link>
              ))}
            </div>
          </div>

          {/* Alerts */}
          {failedRepos.length > 0 && (
            <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.20)' }}>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-xs font-semibold text-red-400">Indexing failures</span>
              </div>
              {failedRepos.map(r => (
                <div key={r._id} className="text-xs text-t3 py-1 border-b last:border-0" style={{ borderColor: 'rgba(239,68,68,0.15)' }}>
                  <span className="text-t1 font-mono">{r.name}</span>
                  {r.errorMessage && <span className="text-red-400/70 ml-2">· {r.errorMessage}</span>}
                </div>
              ))}
              <Link to="/repositories" className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300 mt-3 transition-colors">
                View in repositories <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Suggested questions */}
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(189,187,255,0.18)', border: '1px solid rgba(189,187,255,0.35)' }}>
            <Zap className="w-3 h-3" style={{ color: 'var(--t1)' }} />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-t1">Suggested questions</h2>
            <p className="text-xs text-t4">Based on your repositories and team activity</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {SUGGESTED_QUESTIONS.map(q => (
            <Link key={q}
              to={`/conversations?q=${encodeURIComponent(q)}`}
              className="group flex items-start gap-3 p-3 rounded-lg border transition-all"
              style={{ borderColor: 'var(--b-subtle)', backgroundColor: 'transparent' }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'var(--b-strong)';
                e.currentTarget.style.backgroundColor = 'var(--brand-dim)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--b-subtle)';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <MessageSquare className="w-3.5 h-3.5 text-t4 group-hover:text-t1 mt-0.5 flex-shrink-0 transition-colors" />
              <span className="text-xs text-t2 group-hover:text-t1 leading-relaxed transition-colors">{q}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
